import React, {
  useEffect,
  useState
} from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import { toast } from "react-toastify";
import moment from "moment-timezone";

import {
  IFormOption,
  Select
} from "src/library/components/Forms";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import SelectedCustomer from "src/core/customers/components/SelectedCustomer";
import SelectedDrop from "src/core/drops/components/SelectedDrop";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import {
  FilterByType,
  IFilterType,
  useDashboardApi
} from "src/core/dashboard/api/dashboard.api";
import {
  DateRange,
  SelectedDateRange
} from "src/library/components/SelectedDateRange";

import {
  DashboardGlobalFiltersState,
  DashboardMode,
  useDashboardContext
} from "../../contexts/DashboardContext";
import { transformGlobalFilters } from "../../utils";

export interface DashboardGlobalFiltersProps {
  terminology: ITerminology;
}

export interface DashboardFiltersRef {
  setHasSetInitialDateFilters: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * Global filters component for controlling filters to be used for metric data on admin dashboard
 */
const DashboardGlobalFilters: React.ForwardRefRenderFunction<DashboardFiltersRef, DashboardGlobalFiltersProps> = (
  { terminology }, ref
) => {
  const [ updateDashboardFiltersRes, updateDashboardFiltersReq ] = useDashboardApi("UPDATE_GLOBAL_DASHBOARD_FILTERS");
  const [ dashboardFiltersSaved, setDashboardFiltersSaved ] = useState(true);

  const {
    currentDashboard, globalFilters, filterTypes, updateGlobalFilters, mode
  } = useDashboardContext();

  const [ filterBy, setFitlerBy ] = useState<IFilterType | null>(globalFilters.filterByType || null);

  useEffect(() => {
    setFitlerBy(globalFilters.filterByType || null);
  }, [ globalFilters ]);

  const dateRangeFilterType = React.useMemo(() => {
    return _.find(filterTypes, ft => ft.filterBy === FilterByType.DateRange);
  }, [ filterTypes ]);

  const [ dateRange, setDateRange ] = React.useState<DateRange>({
    start: null,
    end: null
  });

  const [ autoDateDaysFilter, setAutoDateDaysFilter ] = React.useState<number | null>(null);
  const [ hasSetInitialDateFilters, setHasSetInitialDateFilters ] = React.useState(false);

  React.useImperativeHandle(ref, () => ({ setHasSetInitialDateFilters }));

  // Keep local dateRange in sync with dashboard context
  React.useEffect(() => {
    if (!hasSetInitialDateFilters && globalFilters.dateRangeOptions) {
      if (globalFilters.dateRangeOptions?.custom) {
        setDateRange({
          start: moment.utc(globalFilters.dateRangeOptions?.custom.start, "YYYY-MM-DD").toDate(),
          end: moment.utc(globalFilters.dateRangeOptions?.custom.end, "YYYY-MM-DD").toDate()
        });
      } else if (globalFilters.dateRangeOptions?.days) {
        setAutoDateDaysFilter(globalFilters.dateRangeOptions?.days);

        setDateRange({
          start: moment.utc().subtract(globalFilters.dateRangeOptions?.days, "days").startOf("day").toDate(),
          end: moment.utc().endOf("day").toDate()
        });
      }

      setHasSetInitialDateFilters(true);
    }
  }, [ globalFilters, hasSetInitialDateFilters ]);

  // TODOMW: handle deps array
  // Set local filter by value to null when no entity ID is provided before the dashboard is saved
  React.useEffect(() => {
    if (filterBy?.filterBy === FilterByType.Customer && !globalFilters.customer) {
      setFitlerBy(null);
    }

    if (filterBy?.filterBy === FilterByType.Branch && !globalFilters.drop) {
      setFitlerBy(null);
    }

    setDashboardFiltersSaved(true);
  }, [ currentDashboard ]);

  // Handle response when attempting to persist global dashboard filters
  React.useEffect(() => {
    if (updateDashboardFiltersRes.error) {
      toast.error(updateDashboardFiltersRes.error.message);
    }

    if (updateDashboardFiltersRes.data) {
      toast.success("Successfully updated dashboard filters");

      if (filterBy?.filterBy === FilterByType.Customer && !globalFilters.customer) {
        setFitlerBy(null);
      }

      if (filterBy?.filterBy === FilterByType.Branch && !globalFilters.drop) {
        setFitlerBy(null);
      }

      setDashboardFiltersSaved(true);
    }
  }, [ updateDashboardFiltersRes.data, updateDashboardFiltersRes.error ]);

  // Keep customDateRangeSelected in sync with dashboard context
  React.useEffect(() => {
    setAutoDateDaysFilter(globalFilters.dateRangeOptions?.days || null);
  }, [ globalFilters.dateRangeOptions?.days ]);

  // handle changes to customer and drop options
  const applyFilterByType = React.useCallback((entityId: number | undefined, data: ICustomer | IDrop | null | undefined) => {
    const updatedState = { ...globalFilters };
    const selectedType = _.find(filterTypes, ft => ft.filterBy === filterBy?.filterBy);

    if (selectedType) {
      // ensure filterByType type set correctly
      updatedState.filterByType = selectedType;
      updatedState.filterByValue = entityId ? { id: entityId } : null;

      // add the customer to state if selected
      if (selectedType.filterBy === FilterByType.Customer && data) {
        updatedState.customer = data as ICustomer;
        // reset drop state to null when customer selected
        updatedState.drop = null;
      }

      // add the drop to state if selected
      if (selectedType.filterBy === FilterByType.Branch && data) {
        updatedState.drop = data as IDrop;
        // reset customer state to null when drop selected
        updatedState.customer = null;
      }

      // update context state
      updateGlobalFilters(updatedState);
      setDashboardFiltersSaved(false);
    }
  }, [
    filterBy,
    filterTypes,
    globalFilters,
    updateGlobalFilters
  ]);

  // captures filterTypeId and sanitises to send correct data to update filters state
  const handleGlobalFilterTypeSelect = React.useCallback((filterTypeId: string) => {
    if (filterTypeId === "all") {
      // update context state setting customer and drop options to null
      setFitlerBy(null);

      updateGlobalFilters({
        ...globalFilters,
        filterByType: null,
        filterByValue: null,
        customer: null,
        drop: null
      });
      setDashboardFiltersSaved(false);

      return;
    }

    const selectedFilterType = _.find(filterTypes, ft => ft.id === parseInt(filterTypeId));

    if (selectedFilterType) {
      setFitlerBy(selectedFilterType);
    }
  }, [
    filterTypes,
    globalFilters,
    updateGlobalFilters
  ]);

  // handle date range changes
  const applyDateRangeFilter = React.useCallback((dateRangeSelectValue: number| "custom", newRange?: DateRange) => {
    const isCustom = dateRangeSelectValue === "custom";

    const updatedState: DashboardGlobalFiltersState = {
      ...globalFilters,
      dateRangeFilterTypeId: dateRangeFilterType?.id
    };

    if (isCustom && newRange && newRange?.start && newRange?.end) {
      setDateRange(newRange);
      setAutoDateDaysFilter(null);

      updatedState.dateRangeOptions = {
        ...updatedState.dateRangeOptions,
        days: null,
        custom: {
          start: moment.utc(newRange.start).format("YYYY-MM-DD"),
          end: moment.utc(newRange.end).format("YYYY-MM-DD")
        }
      };
    } else if (typeof dateRangeSelectValue === "number") {
      setAutoDateDaysFilter(dateRangeSelectValue);

      setDateRange({
        start: moment.utc().subtract(dateRangeSelectValue, "days").startOf("day").toDate(),
        end: moment.utc().endOf("day").toDate()
      });

      updatedState.dateRangeOptions = {
        ...updatedState.dateRangeOptions,
        custom: null,
        days: dateRangeSelectValue
      };
    }

    updateGlobalFilters(updatedState);
    setDashboardFiltersSaved(false);
  }, [
    dateRangeFilterType?.id,
    globalFilters,
    updateGlobalFilters
  ]);

  // generates options for select filter by type: customer or drop
  const globalFilterByTypeOptions = React.useMemo(() => {
    return _.reduce(filterTypes, (prevFilters, currentFilter) => {
      if (currentFilter.filterBy === FilterByType.Customer || currentFilter.filterBy === FilterByType.Branch) {
        return prevFilters.concat([
          {
            text: _.startCase(currentFilter.filterBy),
            value: currentFilter.id.toString()
          }
        ]);
      }

      return prevFilters;
    }, [] as IFormOption[]);
  }, [ filterTypes ]);

  // grabs the available options off of the filterType dateRange and generates date dropdown options
  const dateRangeOptions = React.useMemo(() => {
    const dateRangeType = _.find(filterTypes, ft => ft.filterBy === FilterByType.DateRange);
    const options = [];

    if (dateRangeType && dateRangeType.filterOptions) {
      for (const key in dateRangeType.filterOptions) {
        const value = dateRangeType.filterOptions[ key ];

        if (typeof value === "number") {
          options.push({
            text: _.startCase(key),
            value: value.toString()
          });
        } else {
          options.push({
            text: _.startCase(key),
            value: "custom"
          });
        }
      }

      return options;
    }

    return [];
  }, [ filterTypes ]);

  // helper for blocking user from saving if filters not set
  const canSave = React.useMemo(() => {
    // if no range is set yet, can't save
    if (!globalFilters?.dateRangeOptions) {
      return false;
    }

    if (!globalFilters?.dateRangeOptions?.days && !globalFilters.dateRangeOptions?.custom) {
      return false;
    }

    return true;
  }, [ globalFilters ]);

  const handleSaveDashboard = React.useCallback(() => {
    if (currentDashboard) {
      updateDashboardFiltersReq({
        dashboardId: currentDashboard.id,
        params: transformGlobalFilters(globalFilters, currentDashboard.id)
      });
    }
  }, [
    currentDashboard,
    globalFilters,
    updateDashboardFiltersReq
  ]);

  return (
    <StyledWrapper className={mode === DashboardMode.Edit || !currentDashboard ? "disabled" : undefined}>
      <div className="header">
        <Text weight="bold">
          Filter By:
        </Text>
      </div>

      <div className="filters">
        <div className="global-filter-type">
          <Select
            as="select"
            name="global-filter-type"
            label="Data Type"
            autoHideLabels
            handleChange={e => handleGlobalFilterTypeSelect(e.target.value)}
            options={globalFilterByTypeOptions}
            value={filterBy?.id || "all"}
            unselectedOption={{
              text: "All",
              value: "all"
            }}
          />

          {filterBy?.filterBy === FilterByType.Customer && (
            <div className="sub-filter">
              <SelectedCustomer
                customer={globalFilters.customer || null}
                updateCustomer={newCustomer => applyFilterByType(newCustomer?.id, newCustomer)}
                terminology={terminology}
                placeHolder={`Please Select ${_.startCase(terminology.customer.singular.text)}`}
                disableRemoveCustomer
                disablePreload
              />
            </div>
          )}

          {filterBy?.filterBy === FilterByType.Branch && (
            <div className="sub-filter selected-drop">
              <SelectedDrop
                drop={globalFilters.drop || null}
                handleUpdate={newDrop => applyFilterByType(newDrop?.id, newDrop)}
                terminology={terminology}
                placeHolder={`Please Select ${_.startCase(terminology.drop.singular.text)}`}
                disableRemoveDrop
              />
            </div>
          )}
        </div>

        <div className="date-range">
          <Select
            as="select"
            name="date-filter-type"
            label="Date Range"
            autoHideLabels
            handleChange={e => {
              if (e.target.value === "custom") {
                applyDateRangeFilter(e.target.value, {
                  start: moment.utc().subtract(autoDateDaysFilter, "days").startOf("day").toDate(),
                  end: moment.utc().endOf("day").toDate()
                });
              } else {
                applyDateRangeFilter(parseInt(e.target.value, 10));
              }
            }}
            options={dateRangeOptions}
            value={autoDateDaysFilter === null ? "custom" : autoDateDaysFilter?.toString() || ""}
            unselectedOption={{
              text: "Please select range",
              value: ""
            }}
          />

          {autoDateDaysFilter === null && dateRange && (
            <SelectedDateRange
              onDateConfirmAction={newRange => applyDateRangeFilter("custom", newRange)}
              range={dateRange}
              maxRangeDays={365}
            />
          )}
        </div>

        <div className="save-button-container">
          <Button
            className={dashboardFiltersSaved ? "save-button-disabled" : undefined}
            onClick={() => handleSaveDashboard()}
            disabled={!canSave}
          >
            Save Filters
          </Button>
        </div>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius};
    padding: ${theme.spacing.padding.md};
    display: flex;
    flex-direction: column;

    .header {
      margin-bottom: ${theme.spacing.margins.md};
    }

    .filters {
      display: flex;
    }

    .global-filter-type {
      min-width: 245px;
      margin-right: ${theme.spacing.margins.lg};
    }

    .global-filter-type,
    .date-range {
      select {
        :hover {
          cursor: pointer;
          border-color: ${theme.colors.midGrey};
        }
      }
    }

    .sub-filter {
      margin-top: ${theme.spacing.margins.md};

      span {
        margin-right: ${theme.spacing.margins.md};
      }
    }

    .selected-customer {
      min-width: 245px;
      border: solid 1px ${theme.colors.lightGrey};
      padding: 0 ${theme.spacing.padding.md};
    }

    .customer-search-component {
      border: solid 1px ${theme.colors.lightGrey};
    }

    .date-range {
      min-width: 245px;
    }

    .selected-date-range {
      margin-top: ${theme.spacing.margins.md};
    }

    .save-button-container {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
    }

    &.disabled, .save-button-disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  `}
`;

export default React.forwardRef(DashboardGlobalFilters);
